// Order of an object's model numbers (REQ-457..459): processing picks the FIRST one that matches a
// detected object, so the list order is meaningful and is sent to the backend as-is.

/**
 * Moves the item at `from` so it lands in the gap `to` refers to, where gaps are counted on the list
 * BEFORE the move (0 = before the first item, `list.length` = after the last). Returns a new array;
 * the input is returned unchanged when the move is a no-op or out of range.
 */
export const moveItem = <T>(list: T[], from: number, to: number): T[] => {
	if (from < 0 || from >= list.length || to < 0 || to > list.length) return list
	const target = to > from ? to - 1 : to
	if (target === from) return list
	const next = list.slice()
	const [item] = next.splice(from, 1)
	next.splice(target, 0, item)
	return next
}

type TTagRect = Pick<DOMRect, "left" | "right" | "top" | "height">

/**
 * Picks the gap nearest to the pointer. The ROW is chosen first — the one whose band contains `y`,
 * else the nearest — and only then the nearest tag edge within it, so a wide tag can never send the
 * drop to the row above or below. Returns the gap index plus where to draw the drop bar, or `null`
 * when there are no tags.
 */
export const resolveDropGap = (rects: TTagRect[], x: number, y: number) => {
	if (!rects.length) return null
	const centerOf = (rect: TTagRect) => rect.top + rect.height / 2
	const inBand = rects.find((rect) => y >= rect.top && y <= rect.top + rect.height)
	const anchor =
		inBand ?? rects.reduce((best, rect) => (Math.abs(y - centerOf(rect)) < Math.abs(y - centerOf(best)) ? rect : best))
	// Tags on one wrapped line share a top; allow a pixel of rounding.
	const isSameRow = (rect: TTagRect) => Math.abs(rect.top - anchor.top) <= 1

	let best: { index: number; x: number; top: number; height: number; distance: number } | null = null
	rects.forEach((rect, i) => {
		if (!isSameRow(rect)) return
		const edges: [number, number][] = [
			[rect.left, i],
			[rect.right, i + 1],
		]
		edges.forEach(([edgeX, index]) => {
			const distance = Math.abs(x - edgeX)
			if (!best || distance < best.distance) best = { index, x: edgeX, top: rect.top, height: rect.height, distance }
		})
	})
	if (!best) return null
	const { index, x: barX, top, height } = best
	return { index, x: barX, top, height }
}

/**
 * Index a dragged row should take in a vertical list, from the vertical midpoints of the OTHER
 * rows (dragged row excluded, top to bottom): the number of rows whose midpoint is above `y`.
 */
export const resolveRowDropIndex = (midpoints: number[], y: number): number => midpoints.filter((mid) => mid < y).length

/** Returns `list` with `item` moved to `index` (clamped). Same reference when nothing moves. */
export const moveItemTo = <T>(list: T[], item: T, index: number): T[] => {
	const from = list.indexOf(item)
	if (from < 0) return list
	const target = Math.max(0, Math.min(index, list.length - 1))
	if (target === from) return list
	const next = list.filter((entry) => entry !== item)
	next.splice(target, 0, item)
	return next
}

/** True when both lists hold the same items in the same order. */
export const isSameOrder = <T>(a: T[], b: T[]): boolean => a.length === b.length && a.every((item, i) => item === b[i])

/**
 * Maps model number names to their ids in list order. Returns `null` when any name has no id —
 * the reorder endpoint needs the FULL list, so a partial one must never be sent.
 */
export const resolveOrderedIds = (names: string[], nameToId: Record<string, number>): number[] | null => {
	const ids = names.map((name) => nameToId[name])
	return ids.every((id) => typeof id === "number") ? ids : null
}

/** `order` followed by any of `names` it is missing, so nothing connected can drop out of view. */
export const completeModelNumberOrder = (order: string[] | undefined, names: string[]): string[] => {
	const base = order ?? []
	const missing = names.filter((name) => !base.includes(name))
	return missing.length ? [...base, ...missing] : base
}
